import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { GitMerge, ArrowLeft, AlertTriangle, Undo2, RefreshCw } from "lucide-react";

const CHAMPS = [
  ["civilite", "Civilité"], ["prenom", "Prénom"], ["nom", "Nom"],
  ["email_principal", "Email principal"], ["telephone", "Téléphone"],
  ["statut_pro", "Profession"], ["rpps_adeli", "RPPS / ADELI"],
  ["profession_libre", "Précision profession"], ["type", "Type"],
  ["statut", "Statut"], ["date_adhesion", "Date d'adhésion"],
  ["date_fin_adhesion", "Fin d'adhésion"],
];

const CONFIANCE_CLS = {
  "Élevée": "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30",
  "Moyenne": "bg-cfhtb-accent/15 text-cfhtb-accent border-cfhtb-accent/30",
  "À vérifier": "bg-slate-100 text-slate-600 border-slate-200",
};

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return new Date(v).toLocaleDateString("fr-FR");
  }
  return String(v);
};

export default function DoublonsAdmin() {
  const [paires, setPaires] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [fusion, setFusion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ignores, setIgnores] = useState([]);

  const load = () => {
    api("/admin/fusions/doublons").then(setPaires).catch((e) => toast.error(e.message));
    api("/admin/fusions").then(setHistorique).catch(() => {});
    api("/admin/fusions/ignorees").then(setIgnores).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const ouvrir = (paire) => {
    const garderKey = paire.person_a.statut === "ACTIF" ? "a"
      : paire.person_b.statut === "ACTIF" ? "b" : "a";
    setFusion({ paire, garderKey, choix: {} });
  };

  const choixPour = (champ) => {
    if (fusion.choix[champ]) return fusion.choix[champ];
    const g = fusion.garderKey === "a" ? fusion.paire.person_a : fusion.paire.person_b;
    const ab = fusion.garderKey === "a" ? fusion.paire.person_b : fusion.paire.person_a;
    if ((g[champ] === null || g[champ] === undefined || g[champ] === "") &&
        (ab[champ] !== null && ab[champ] !== undefined && ab[champ] !== "")) return "absorber";
    return "garder";
  };

  const fusionner = async () => {
    const g = fusion.garderKey === "a" ? fusion.paire.person_a : fusion.paire.person_b;
    const ab = fusion.garderKey === "a" ? fusion.paire.person_b : fusion.paire.person_a;
    if (!window.confirm(
      `Fusionner définitivement ?\n\nConservée : ${g.prenom} ${g.nom} <${g.email_principal}>\nAbsorbée : ${ab.prenom} ${ab.nom} <${ab.email_principal}>\n\nLa fiche absorbée passera en statut FUSIONNE. Annulation possible pendant 30 jours.`)) return;
    setBusy(true);
    const champs = {};
    CHAMPS.forEach(([c]) => { champs[c] = choixPour(c); });
    try {
      await api("/admin/fusions", { method: "POST", body: { garder_id: g.id, absorber_id: ab.id, champs } });
      toast.success("Fusion effectuée");
      setFusion(null);
      setPaires(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const annuler = async (f) => {
    if (!window.confirm(`Annuler la fusion ?\n\n${f.absorber_label} redeviendra une fiche active et ${f.garder_label} retrouvera ses données d'avant fusion.`)) return;
    try {
      await api(`/admin/fusions/${f.id}/annuler`, { method: "POST" });
      toast.success("Fusion annulée");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const ignorer = async (p) => {
    if (!window.confirm(`Garder ces 2 fiches séparées ?\n\n${p.person_a.prenom} ${p.person_a.nom} et ${p.person_b.prenom} ${p.person_b.nom} ne seront plus proposés comme doublon.`)) return;
    try {
      await api("/admin/fusions/ignorer", { method: "POST", body: { a_id: p.person_a.id, b_id: p.person_b.id } });
      toast.success("Doublon ignoré — les 2 fiches sont conservées");
      setPaires(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const reactiver = async (ig) => {
    try {
      await api(`/admin/fusions/ignorees/${ig.id}/reactiver`, { method: "POST" });
      toast.success("Doublon réaffiché");
      setPaires(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (fusion) {
    const g = fusion.garderKey === "a" ? fusion.paire.person_a : fusion.paire.person_b;
    const ab = fusion.garderKey === "a" ? fusion.paire.person_b : fusion.paire.person_a;
    return (
      <AppLayout variant="admin">
        <div data-testid="fusion-compare" className="space-y-5 animate-fade-in">
          <button onClick={() => setFusion(null)} data-testid="fusion-retour"
            className="inline-flex items-center gap-2 text-sm text-cfhtb-text hover:text-cfhtb-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Retour aux doublons
          </button>
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Fusionner deux fiches</h1>
            <p className="text-sm text-cfhtb-text mt-1">
              {fusion.paire.raisons.join(" · ")} — choisissez pour chaque champ la valeur à conserver.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4">
            <span className="text-sm font-medium text-cfhtb-heading">Fiche conservée :</span>
            {["a", "b"].map((k) => {
              const p = k === "a" ? fusion.paire.person_a : fusion.paire.person_b;
              return (
                <button key={k} data-testid={`fusion-garder-${k}`}
                  onClick={() => setFusion({ ...fusion, garderKey: k, choix: {} })}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    fusion.garderKey === k
                      ? "bg-cfhtb-primary text-white"
                      : "bg-cfhtb-bg text-cfhtb-text hover:bg-cfhtb-light/30"}`}>
                  {p.prenom} {p.nom} ({p.email_principal})
                </button>
              );
            })}
          </div>

          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-cfhtb-bg/40">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Champ</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-success px-4 py-3">Conservée : {g.prenom} {g.nom}</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-accent px-4 py-3">Absorbée : {ab.prenom} {ab.nom}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-testid="fusion-champs">
                {CHAMPS.map(([champ, label]) => (
                  <tr key={champ} data-testid={`fusion-champ-${champ}`}>
                    <td className="px-4 py-2.5 text-cfhtb-heading font-medium">{label}</td>
                    {[["garder", g], ["absorber", ab]].map(([side, p]) => (
                      <td key={side} className="px-4 py-2.5">
                        <label className={`flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 transition-colors ${
                          choixPour(champ) === side ? "bg-cfhtb-primary/5 ring-1 ring-cfhtb-primary/30" : "hover:bg-cfhtb-bg/50"}`}>
                          <input type="radio" name={`champ-${champ}`} data-testid={`fusion-${champ}-${side}`}
                            checked={choixPour(champ) === side}
                            onChange={() => setFusion({ ...fusion, choix: { ...fusion.choix, [champ]: side } })}
                            className="text-cfhtb-primary focus:ring-cfhtb-light" />
                          <span className="text-cfhtb-text">{fmt(p[champ])}</span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-2.5 text-cfhtb-heading font-medium">Emails secondaires / tags / consentements</td>
                  <td colSpan={2} className="px-4 py-2.5 text-xs text-cfhtb-text">
                    Fusionnés automatiquement (union des emails secondaires et des tags, consentement le plus favorable conservé).
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5 text-cfhtb-heading font-medium">Inscriptions événements & fiche annuaire</td>
                  <td colSpan={2} className="px-4 py-2.5 text-xs text-cfhtb-text">
                    Transférées sur la fiche conservée (accès replay préservés ; la fiche annuaire est transférée si la fiche conservée n'en a pas).
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex items-start gap-3 bg-cfhtb-accent/10 border border-cfhtb-accent/30 rounded-lg p-4">
            <AlertTriangle className="h-5 w-5 text-cfhtb-accent shrink-0 mt-0.5" />
            <p className="text-sm text-cfhtb-heading">
              La fiche absorbée passera au statut <strong>FUSIONNE</strong> et ses sessions seront fermées.
              Cette action est <strong>annulable pendant 30 jours</strong> depuis l'historique des fusions.
            </p>
          </div>

          <div className="flex justify-end">
            <button onClick={fusionner} disabled={busy} data-testid="fusion-confirmer"
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60">
              <GitMerge className="h-4 w-4" />
              {busy ? "Fusion en cours…" : "Fusionner les deux fiches"}
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout variant="admin">
      <div data-testid="doublons-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <GitMerge className="h-7 w-7 text-cfhtb-primary" />
            <div>
              <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Fusion de membres</h1>
              <p className="text-sm text-cfhtb-text">
                Doublons détectés automatiquement (même email, même nom + prénom ou même téléphone). Chaque fusion est validée manuellement.
              </p>
            </div>
          </div>
          <button onClick={() => { setPaires(null); load(); }} data-testid="doublons-refresh"
            className="inline-flex items-center gap-2 bg-white border border-slate-300 text-cfhtb-text rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors">
            <RefreshCw className="h-4 w-4" /> Actualiser
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {!paires ? (
            <p className="px-4 py-10 text-center text-sm text-cfhtb-text">Analyse en cours…</p>
          ) : paires.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="doublons-empty">
              Aucun doublon détecté.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="doublons-list">
              {paires.map((p, i) => (
                <li key={`${p.person_a.id}-${p.person_b.id}`}
                  className="px-4 py-3 flex flex-wrap items-center gap-3 hover:bg-cfhtb-bg/40 transition-colors"
                  data-testid={`doublon-paire-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-cfhtb-heading">
                      {p.person_a.prenom} {p.person_a.nom} <span className="text-cfhtb-text font-normal">({p.person_a.email_principal})</span>
                      <span className="mx-1.5 text-cfhtb-light">⇄</span>
                      {p.person_b.prenom} {p.person_b.nom} <span className="text-cfhtb-text font-normal">({p.person_b.email_principal})</span>
                    </p>
                    <p className="text-xs text-cfhtb-text mt-0.5">
                      {p.raisons.join(" · ")} · statuts : {p.person_a.statut} / {p.person_b.statut}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${CONFIANCE_CLS[p.confiance]}`}
                    data-testid={`doublon-confiance-${i}`}>
                    Confiance : {p.confiance}
                  </span>
                  <button onClick={() => ignorer(p)} data-testid={`doublon-ignorer-${i}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-cfhtb-text rounded-md px-3 py-1.5 text-xs font-medium hover:bg-slate-50 transition-colors">
                    Garder les 2
                  </button>
                  <button onClick={() => ouvrir(p)} data-testid={`doublon-comparer-${i}`}
                    className="inline-flex items-center gap-1.5 bg-cfhtb-primary text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-[#1f1975] transition-colors">
                    <GitMerge className="h-3.5 w-3.5" /> Comparer & fusionner
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {ignores.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="doublons-ignores">
            <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
              <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Doublons ignorés (2 fiches conservées)</h2>
            </div>
            <ul className="divide-y divide-slate-100">
              {ignores.map((ig) => (
                <li key={ig.id} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`ignore-row-${ig.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-cfhtb-heading">
                      <span className="font-medium">{ig.a_label}</span>
                      <span className="mx-1.5 text-cfhtb-light">⇄</span>
                      <span className="font-medium">{ig.b_label}</span>
                    </p>
                  </div>
                  <button onClick={() => reactiver(ig)} data-testid={`ignore-reactiver-${ig.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-cfhtb-text border border-slate-300 rounded-md px-2.5 py-1.5 hover:bg-slate-50 transition-colors">
                    <Undo2 className="h-3.5 w-3.5" /> Réafficher comme doublon
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="fusions-historique">
          <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
            <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Historique des fusions</h2>
          </div>
          {historique.length === 0 ? (
            <p className="px-5 py-6 text-sm text-cfhtb-text" data-testid="fusions-empty">Aucune fusion effectuée.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {historique.map((f) => (
                <li key={f.id} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`fusion-row-${f.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-cfhtb-heading">
                      <span className="font-medium">{f.absorber_label}</span>
                      <span className="text-cfhtb-text"> → fusionnée dans → </span>
                      <span className="font-medium">{f.garder_label}</span>
                    </p>
                    <p className="text-xs text-cfhtb-text mt-0.5">
                      {new Date(f.effectue_le).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  {f.annule_le ? (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Annulée</span>
                  ) : f.annulable ? (
                    <button onClick={() => annuler(f)} data-testid={`fusion-annuler-${f.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-cfhtb-error border border-cfhtb-error/40 rounded-md px-2.5 py-1.5 hover:bg-cfhtb-error/5 transition-colors">
                      <Undo2 className="h-3.5 w-3.5" /> Annuler (30 j)
                    </button>
                  ) : (
                    <span className="text-xs text-cfhtb-text">Délai d'annulation dépassé</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
